import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ChevronRight, PackageSearch } from 'lucide-react';
import { ToneBadge } from './receivingAtoms';
import PoDetailDrawer from './PoDetailDrawer';
import { useReceivingStore } from './ReceivingStore';
import { mapLogToSession } from './receivingModel';
import { fmtDateTime } from './receivingStatus';
import { getPoReceivedLogsRequest } from '../../slices/boxReceivedSlice';
import { LoaderSpinner } from '../../components/LoaderSpinner';
import { SelectDropdown } from '../../components/SelectDropdown';
import { useIsMobile } from '../../helpers/useIsMobile';

function worstResult(results) {
  if (results.includes('duplicate_epc')) return 'duplicate_epc';
  if (results.includes('short')) return 'short';
  if (results.includes('over')) return 'over';
  return 'complete';
}

function ReceivingScanLogTab({ refreshKey = 0 }) {
  const { pos, hydrate, ensurePoLoaded } = useReceivingStore();
  const reduxDispatch = useDispatch();
  const posRef = useRef(pos);
  posRef.current = pos;
  const apiLogs = useSelector((state) => state.boxReceived.logs);
  const logsLoading = useSelector((state) => state.boxReceived.logsLoading);
  const [query, setQuery] = useState('');
  const [resultFilter, setResultFilter] = useState('all');
  const [openPo, setOpenPo] = useState(null);
  const [expanded, setExpanded] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const isMobile = useIsMobile();

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      // Paint immediately when the store already holds queue data (e.g. from
      // the session cache or a prior tab visit); refresh runs in background.
      setLoading(!posRef.current.length);
      try {
        await Promise.all([
          hydrate(),
          reduxDispatch(getPoReceivedLogsRequest({})).unwrap(),
        ]);
        if (cancelled) return;
      } catch (err) {
        if (!cancelled) console.error('Failed to load scan log', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [refreshKey, hydrate, reduxDispatch]);

  const toggleExpanded = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  /**
   * One row per PO: all scan sessions (from store + API) nested under that PO,
   * with boxes resolved from the hydrated ReceivingPo model.
   */
  const poGroups = useMemo(() => {
    const poById = new Map(pos.map((p) => [p.id, p]));
    const poByDbId = new Map(pos.filter((p) => p.poDbId).map((p) => [p.poDbId, p]));
    const groups = new Map();

    const ensureGroup = (poNumber, poDbId, supplier, poFromStore) => {
      const key = poNumber || poDbId;
      if (!groups.has(key)) {
        groups.set(key, {
          key,
          poId: poNumber,
          poDbId,
          supplier: supplier || poFromStore?.supplier || 'Unknown',
          po: poFromStore || null,
          sessions: [],
        });
      }
      const g = groups.get(key);
      if (poFromStore && (!g.po || (poFromStore.boxes?.length || 0) > (g.po.boxes?.length || 0))) {
        g.po = poFromStore;
        g.supplier = poFromStore.supplier || g.supplier;
      }
      return g;
    };

    // Prefer sessions already attached on hydrated POs (with boxIds)
    for (const po of pos) {
      for (const s of po.scanSessions || []) {
        const g = ensureGroup(po.id, po.poDbId, po.supplier, po);
        if (!g.sessions.some((x) => x.logId && s.logId && x.logId === s.logId)) {
          g.sessions.push({
            ...s,
            box: po.boxes.find((b) => b.id === s.boxId) || null,
          });
        }
      }
    }

    // Merge API logs not already present (e.g. POs outside current queue page)
    for (const log of apiLogs) {
      const poNumber = log.purchaseOrder?.po_number;
      const poDbId = log.po_id || log.purchaseOrder?.id;
      const po =
        poById.get(poNumber) ||
        poByDbId.get(poDbId) ||
        null;
      const g = ensureGroup(
        poNumber,
        poDbId,
        log.purchaseOrder?.Vendor?.vendor_name,
        po,
      );
      if (g.sessions.some((x) => x.logId === log.id)) continue;
      const session = mapLogToSession(log, poNumber, po?.boxes || []);
      g.sessions.push({
        ...session,
        box: (po?.boxes || []).find((b) => b.id === session.boxId) || null,
      });
    }

    return Array.from(groups.values())
      .map((g) => {
        const sessions = [...g.sessions].sort(
          (a, b) => new Date(b.timestamp) - new Date(a.timestamp),
        );
        const results = sessions.map((s) => s.result);
        const boxesTouched = [];
        const seenBox = new Set();
        for (const s of sessions) {
          if (s.box && !seenBox.has(s.box.id)) {
            seenBox.add(s.box.id);
            boxesTouched.push(s.box);
          }
        }
        // Fall back to all PO boxes that have any scan activity
        const poBoxes = g.po?.boxes || [];
        const boxes =
          boxesTouched.length > 0
            ? boxesTouched
            : poBoxes.filter((b) => b.scannedUnits != null || b.state !== 'pending');

        const skus = [
          ...new Set(
            sessions.flatMap((s) => (s.items || []).map((i) => i.sku).filter(Boolean)),
          ),
        ];

        return {
          ...g,
          sessions,
          boxes,
          allBoxes: poBoxes,
          skus,
          timestamp: sessions[0]?.timestamp,
          scannedBy: sessions[0]?.scannedBy || '—',
          rawReads: sessions.reduce((n, s) => n + (s.rawReads || 0), 0),
          uniqueEpcs: sessions.reduce((n, s) => n + (s.uniqueEpcs ?? s.matched ?? 0), 0),
          matched: sessions.reduce((n, s) => n + (s.matched || 0), 0),
          result: worstResult(results),
          sessionCount: sessions.length,
          boxCount: (boxes.length || poBoxes.length) || sessions.length,
        };
      })
      .filter((g) => {
        if (!g.sessions.length) return false;
        if (resultFilter !== 'all' && g.result !== resultFilter) return false;
        if (query.trim()) {
          const q = query.trim().toLowerCase();
          const hay = [
            g.poId,
            g.supplier,
            g.scannedBy,
            ...g.skus,
            ...(g.sessions || []).flatMap((s) => (s.items || []).map((i) => i.sku)),
          ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();
          if (!hay.includes(q)) return false;
        }
        return true;
      })
      .sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0));
  }, [pos, apiLogs, query, resultFilter]);

  const openPoDrawer = async (group) => {
    const poId = group.po?.id || group.poId;
    if (!poId) return;
    setOpenPo(poId);
    // Load full PO + all line/box cards for the drawer (not just one session)
    await ensurePoLoaded(poId);
  };

  const resultTone = (result) => {
    if (result === 'complete') return 'ok';
    if (result === 'duplicate_epc') return 'danger';
    return 'warn';
  };

  const showLoading = (loading || logsLoading) && poGroups.length === 0;

  return (
    <div className="flex min-h-0 flex-1">
      <div className="flex min-w-0 flex-1 flex-col">
        <div className={`border-b border-gray-200 bg-white ${isMobile ? 'flex flex-col gap-1.5 px-3 py-2' : 'flex flex-wrap items-center gap-2 px-5 py-2.5'}`}>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search PO, supplier, SKU, operator…"
            className={`h-8 rounded border border-gray-300 px-3 text-base ${isMobile ? 'w-full' : 'w-64'}`}
          />
          <div className='w-48 relative'>
            <SelectDropdown
              options={[
                { value: 'all', label: 'All results' }, 
                { value: 'complete', label: 'Complete' }, 
                { value: 'short', label: 'Short' }, 
                { value: 'over', label: 'Over' }, 
                { value: 'duplicate_epc', label: 'Duplicate EPC' }
              ]}
              value={resultFilter}
              onChange={(value) => setResultFilter(value)}
              selectClassname='h-8 rounded border border-gray-300 px-2 text-sm !bg-white'
              renderOption={(option) => <span>{option.label}</span>}
              itemValueClassname='text-sm !px-2'
            />
          </div>
          <span className={isMobile ? 'text-sm text-gray-500' : 'ml-auto text-sm text-gray-500'}>
            Grouped by PO ·{' '}
            <span className="tabular-nums">{poGroups.length}</span> PO
            {poGroups.length !== 1 ? 's' : ''} ·{' '}
            <span className="tabular-nums">
              {poGroups.reduce((n, g) => n + g.sessionCount, 0)}
            </span>{' '}
            sessions
          </span>
        </div>

        <div className="min-h-0 flex-1 overflow-auto">
          {showLoading ? (
            <div className='flex h-full items-center justify-center'>
              <LoaderSpinner height='80' width='80' colorSpinner='black' border='5' />
            </div>
          ) : poGroups.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center gap-2 p-10 text-center">
              <PackageSearch className="h-8 w-8 text-gray-300" />
              <p className="text-base font-medium text-gray-900">No scan sessions match</p>
              <p className="text-sm text-gray-500">Adjust filters or run a new scan from the Queue tab.</p>
            </div>
          ) : (
            <table className={`w-full border-separate border-spacing-0 text-base ${isMobile ? 'min-w-0' : 'min-w-[900px]'}`}>
              <thead>
                <tr className="text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                  <th className="sticky top-0 z-10 w-8 bg-white px-2 py-2" />
                  <th className="sticky top-0 z-10 bg-white px-3 py-2">Scanned at</th>
                  <th className="sticky top-0 z-10 bg-white px-3 py-2">PO / Boxes</th>
                  {!isMobile && <th className="sticky top-0 z-10 bg-white px-3 py-2">Supplier</th>}
                  {!isMobile && <th className="sticky top-0 z-10 bg-white px-3 py-2">SKU</th>}
                  {!isMobile && <th className="sticky top-0 z-10 bg-white px-3 py-2 text-right">Raw reads</th>}
                  <th className="sticky top-0 z-10 bg-white px-3 py-2 text-right">{isMobile ? 'EPCs' : 'Unique EPCs'}</th>
                  {!isMobile && <th className="sticky top-0 z-10 bg-white px-3 py-2 text-right">Matched</th>}
                  <th className="sticky top-0 z-10 bg-white px-3 py-2">Result</th>
                  {!isMobile && <th className="sticky top-0 z-10 bg-white px-3 py-2">By</th>}
                </tr>
              </thead>
              <tbody>
                {poGroups.map((g) => {
                  const isExpanded = expanded.has(g.key);
                  const isSelected = openPo === (g.po?.id || g.poId);
                  const boxLabel =
                    g.allBoxes?.length > 0
                      ? `${g.boxes.length || g.sessions.length}/${g.allBoxes.length} boxes`
                      : `${g.sessionCount} session${g.sessionCount !== 1 ? 's' : ''}`;
                  return (
                    <React.Fragment key={g.key}>
                      <tr
                        className={`cursor-pointer transition-colors hover:bg-gray-50 ${
                          isSelected ? 'bg-blue-50' : ''
                        }`}
                        onClick={() => openPoDrawer(g)}
                      >
                        <td className="px-2 py-2" onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            aria-label={isExpanded ? 'Collapse sessions' : 'Expand sessions'}
                            className="flex h-5 w-5 items-center justify-center rounded text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                            onClick={() => toggleExpanded(g.key)}
                          >
                            <ChevronRight
                              className={`h-3.5 w-3.5 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                            />
                          </button>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-gray-600 tabular-nums">
                          {fmtDateTime(g.timestamp)}
                        </td>
                        <td className="px-3 py-2">
                          <span className="font-mono font-semibold">{g.poId}</span>
                          <span className="ml-1.5 text-sm text-gray-500">{boxLabel}</span>
                        </td>
                        {!isMobile && <td className="max-w-[220px] truncate px-3 py-2">{g.supplier}</td>}
                        {!isMobile && (
                          <td className="px-3 py-2 font-mono text-sm">
                            {g.skus.length <= 2
                              ? g.skus.join(', ') || '—'
                              : `${g.skus.slice(0, 2).join(', ')} +${g.skus.length - 2}`}
                          </td>
                        )}
                        {!isMobile && (
                          <td className="px-3 py-2 text-right font-mono tabular-nums text-gray-600">
                            {g.rawReads.toLocaleString()}
                          </td>
                        )}
                        <td className="px-3 py-2 text-right font-mono tabular-nums font-medium">
                          {g.uniqueEpcs}
                        </td>
                        {!isMobile && (
                          <td
                            className={`px-3 py-2 text-right font-mono tabular-nums font-semibold ${
                              g.result === 'complete' ? 'text-green-700' : 'text-amber-700'
                            }`}
                          >
                            {g.matched}
                          </td>
                        )}
                        <td className="px-3 py-2">
                          <ToneBadge tone={resultTone(g.result)}>
                            {(g.result || 'complete').replace('_', ' ')}
                          </ToneBadge>
                        </td>
                        {!isMobile && <td className="px-3 py-2 text-sm text-gray-600">{g.scannedBy}</td>}
                      </tr>
                      {isExpanded && (
                        <tr className="bg-gray-50">
                          <td />
                          <td colSpan={isMobile ? 4 : 9} className="px-3 pb-3 pt-1">
                            <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
                              Scan sessions on this PO
                            </div>
                            <div className="space-y-3">
                              {g.sessions.map((s) => (
                                <div
                                  key={s.id}
                                  className="rounded border border-gray-200 bg-white px-3 py-2"
                                >
                                  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-sm">
                                    <span className="font-medium text-gray-900">
                                      {fmtDateTime(s.timestamp)}
                                    </span>
                                    {s.box ? (
                                      <span className="text-gray-600">
                                        Box {s.box.boxNumber} · {s.box.sku}
                                      </span>
                                    ) : (
                                      <span className="text-gray-500">
                                        {(s.items || [])
                                          .filter((i) => i.scannedUnits > 0)
                                          .map((i) => i.sku)
                                          .join(', ') || 'Session'}
                                      </span>
                                    )}
                                    <span className="tabular-nums text-gray-500">
                                      raw {s.rawReads} · matched {s.matched}
                                    </span>
                                    <ToneBadge tone={resultTone(s.result)}>
                                      {(s.result || 'complete').replace('_', ' ')}
                                    </ToneBadge>
                                    <span className="text-gray-400">By {s.scannedBy}</span>
                                    {s.logId != null && (
                                      <span className="text-gray-400">Log ID {s.logId}</span>
                                    )}
                                  </div>
                                  {s.items?.length > 0 && (
                                    <table className="mt-2 w-full text-sm">
                                      <thead>
                                        <tr className="border-b border-gray-100 text-left text-xs font-semibold uppercase tracking-wide text-gray-400">
                                          <th className="px-1 py-1">SKU</th>
                                          <th className="px-1 py-1">Product</th>
                                          <th className="px-1 py-1 text-right">Expected</th>
                                          <th className="px-1 py-1 text-right">Scanned</th>
                                          <th className="px-1 py-1 text-right">Variance</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {s.items.map((it) => {
                                          const variance =
                                            (it.scannedUnits || 0) - (it.expectedUnits || 0);
                                          return (
                                            <tr key={it.sku} className="border-b border-gray-50">
                                              <td className="px-1 py-1 font-mono">{it.sku}</td>
                                              <td className="px-1 py-1">{it.productName}</td>
                                              <td className="px-1 py-1 text-right tabular-nums">
                                                {it.expectedUnits}
                                              </td>
                                              <td className="px-1 py-1 text-right tabular-nums font-medium">
                                                {it.scannedUnits}
                                              </td>
                                              <td
                                                className={`px-1 py-1 text-right font-semibold tabular-nums ${
                                                  variance === 0
                                                    ? 'text-green-700'
                                                    : 'text-amber-700'
                                                }`}
                                              >
                                                {variance > 0 ? '+' : ''}
                                                {variance}
                                              </td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  )}
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <PoDetailDrawer poId={openPo} onClose={() => setOpenPo(null)} />
    </div>
  );
}

export default ReceivingScanLogTab;
